import { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../config/firebase';
import { subscriptionService } from '../services/subscriptionService';

export interface SubscriptionStatus {
  hasSubscription: boolean;
  isActive: boolean;
  plan?: string;
  status?: string;
  trialEnd?: string;
  needsPayment?: boolean;
  isNewProspect?: boolean;
  hasSubscriptionHistory?: boolean;
  loading: boolean;
  error?: string;
}

/**
 * Hook to check subscription status for authenticated users
 * Used in login flow to determine if user needs to subscribe
 */
export const useSubscriptionStatus = () => {
  const [user, loading, error] = useAuthState(auth);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    hasSubscription: false,
    isActive: false,
    loading: true
  });

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (loading) return; // Wait for auth to load
      
      if (error) {
        setSubscriptionStatus({
          hasSubscription: false,
          isActive: false,
          loading: false,
          error: 'Authentication error'
        });
        return;
      }

      if (!user) {
        setSubscriptionStatus({
          hasSubscription: false,
          isActive: false,
          loading: false
        });
        return;
      }

      try {
        setSubscriptionStatus(prev => ({ ...prev, loading: true }));
        
        const status = await subscriptionService.getSubscriptionStatus();
        
        setSubscriptionStatus({
          ...status,
          loading: false
        });
      } catch (err) {
        console.error('Error checking subscription status:', err);
        setSubscriptionStatus({
          hasSubscription: false,
          isActive: false,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to check subscription'
        });
      }
    };

    checkSubscriptionStatus();
  }, [user, loading, error]);

  return subscriptionStatus;
};

/**
 * Hook to check if user has an active subscription
 * Simplified version for quick checks
 */
export const useHasActiveSubscription = (): boolean => {
  const status = useSubscriptionStatus();
  return status.isActive;
};

/**
 * Hook to check if user needs to subscribe
 * Returns true if user is authenticated but has no active subscription
 */
export const useNeedsSubscription = (): boolean => {
  const [user] = useAuthState(auth);
  const status = useSubscriptionStatus();
  
  return !!(user && !status.loading && !status.isActive);
}; 